import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../services/api'; // Use your custom Axios instance

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data.data.user);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to fetch user information');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="animate-spin w-10 h-10 text-pink-500" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center mt-10 text-red-500">
        {error || 'Failed to load profile.'}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 text-white">
      <h1 className="text-3xl font-bold mb-6 text-black">Profile</h1>

      {/* Avatar Section */}
      <div className="flex items-center space-x-4 mb-6">
        {user.avatar ? (
          <img
            src={user.avatar}
            alt="Avatar"
            className="w-20 h-20 rounded-full object-cover border border-gray-300"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center text-xl font-semibold">
            {user.firstName[0]}
          </div>
        )}
        <div>
          <p className="text-xl font-semibold text-black">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-sm text-gray-300">{user.email}</p>
          <p className="text-sm text-gray-400">{user.phone}</p>
        </div>
      </div>

      {/* Basic Info Section */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <p className="text-sm text-gray-400">Role</p>
          <p className="text-base text-black">{user.role}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Verified</p>
          <p className="text-base text-black">{user.isVerified ? 'Yes' : 'No'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Active</p>
          <p className="text-base text-black">{user.isActive ? 'Yes' : 'No'}</p>
        </div>
        {user.address?.country && (
          <div>
            <p className="text-sm text-gray-400">Country</p>
            <p className="text-base text-black">{user.address.country}</p>
          </div>
        )}
      </div>

      {/* Role-specific Sections */}
      {user.role === 'store_owner' && user.storeId && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2 text-black">Store Details</h2>
          <p className=' text-black'>
            <span className="font-medium text-black">Name:</span> {user.storeId.name}
          </p>
          <p className=' text-black'>
            <span className="font-medium text-black">Slug:</span> {user.storeId.slug}
          </p>
          {user.storeId.logo && (
            <img
              src={user.storeId.logo}
              alt="Store Logo"
              className="mt-2 w-24 h-24 object-contain"
            />
          )}
        </div>
      )}

      {user.role === 'manufacturer' && user.manufacturerId && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Manufacturer Details</h2>
          <p>
            <span className="font-medium">Business Name:</span> {user.manufacturerId.businessName}
          </p>
          {user.manufacturerId.logo && (
            <img
              src={user.manufacturerId.logo}
              alt="Manufacturer Logo"
              className="mt-2 w-24 h-24 object-contain"
            />
          )}
        </div>
      )}

      {user.role === 'buyer' && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Preferences</h2>
          <p>
            <span className="font-medium">Email Notifications:</span>{' '}
            {user.preferences?.emailNotifications ? 'Enabled' : 'Disabled'}
          </p>
          <p>
            <span className="font-medium">Push Notifications:</span>{' '}
            {user.preferences?.pushNotifications ? 'Enabled' : 'Disabled'}
          </p>
        </div>
      )}
    </div>
  );
}